/**
 * OperationsCalendarTab — global Zignagenix-wide Operations Calendar
 * (Phase 5f, Jun 11 2026 — Ana spec §6, Daniel's 2026-06-10 expansion).
 *
 * Replaces the standalone Treatment Calendar concept. Aggregates six
 * RBAC-filtered event layers (procedures, follow-ups, patient-requested,
 * appointments, sales-closed, logistics) from operations-calendar:events.
 * The server already filters layers by the user's source permissions and
 * scopes PEM/sales rows; the chips here only toggle DISPLAY of layers the
 * server returned.
 *
 * Views: day / week / month / year. Primary grouping axis: clinic —
 * the dropdown switches between a single clinic and the all-clinics view
 * (clinic badge shown per event when aggregating).
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Calendar, ChevronLeft, ChevronRight, MapPin, X, Layers as LayersIcon,
} from 'lucide-react';
import { cardWrap, neuShadow, neuShadowInset } from './crmShared';

const DAY_MS = 24 * 60 * 60 * 1000;
const VIEWS = ['day', 'week', 'month', 'year'];
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// Mirrors routes/_operations_calendar.js LAYERS (label + color come from
// the server per event; this is only the chip order fallback).
const LAYER_ORDER = ['procedures', 'followups', 'requests', 'appointments', 'sales', 'logistics'];

function startOfDay(d) { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; }
function startOfWeek(d) {
  const x = startOfDay(d);
  const dow = (x.getDay() + 6) % 7; // Monday = 0
  return new Date(x.getTime() - dow * DAY_MS);
}
function startOfMonth(d) { const x = startOfDay(d); x.setDate(1); return x; }
function startOfYear(d) { const x = startOfDay(d); x.setMonth(0, 1); return x; }
function addDays(d, n) { return new Date(d.getTime() + n * DAY_MS); }
function addMonths(d, n) { const x = new Date(d); x.setMonth(x.getMonth() + n); return x; }
function dayKey(d) {
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
}
function fmtTime(d) {
  return new Date(d).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}
function fmtDateLong(d) {
  return new Date(d).toLocaleDateString([], { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
}

// Window for the current view (month fetches the full 6-week grid).
function windowFor(view, anchor) {
  if (view === 'day') { const f = startOfDay(anchor); return [f, addDays(f, 1)]; }
  if (view === 'week') { const f = startOfWeek(anchor); return [f, addDays(f, 7)]; }
  if (view === 'month') {
    const first = startOfMonth(anchor);
    const gridStart = startOfWeek(first);
    return [gridStart, addDays(gridStart, 42)];
  }
  const f = startOfYear(anchor);
  return [f, startOfYear(addMonths(f, 12))];
}

export default function OperationsCalendarTab() {
  const [view, setView] = useState('month');
  const [anchor, setAnchor] = useState(() => new Date());
  const [clinicId, setClinicId] = useState('all');
  const [clinics, setClinics] = useState([]);
  const [events, setEvents] = useState([]);
  const [serverLayers, setServerLayers] = useState([]);
  const [hiddenLayers, setHiddenLayers] = useState(() => new Set());
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Clinics for the dropdown (once).
  useEffect(() => {
    let gone = false;
    (async () => {
      try {
        const r = await window.zignagenixAPI?.invoke?.('operations-calendar:clinics');
        if (!gone && Array.isArray(r)) setClinics(r);
      } catch (_) { /* dropdown degrades to All clinics */ }
    })();
    return () => { gone = true; };
  }, []);

  // Events for the current window.
  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [from, to] = windowFor(view, anchor);
      const r = await window.zignagenixAPI?.invoke?.('operations-calendar:events', {
        from: from.toISOString(), to: to.toISOString(),
        clinicId: clinicId === 'all' ? null : clinicId,
      });
      if (r && !r.error) {
        setEvents(Array.isArray(r.events) ? r.events : []);
        setServerLayers(Array.isArray(r.layers) ? r.layers : []);
      } else {
        setError(r?.message || r?.code || 'Failed to load calendar');
      }
    } catch (e) {
      setError(e?.message || 'Failed to load calendar');
    } finally { setLoading(false); }
  }, [view, anchor, clinicId]);

  useEffect(() => { load(); }, [load]);

  const visible = useMemo(
    () => events.filter((e) => !hiddenLayers.has(e.layer)),
    [events, hiddenLayers]
  );

  const byDay = useMemo(() => {
    const m = new Map();
    for (const e of visible) {
      const k = dayKey(e.start);
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(e);
    }
    return m;
  }, [visible]);

  // Layer chips: every layer the server returned, with counts.
  const layerChips = useMemo(() => {
    const counts = {};
    for (const e of events) counts[e.layer] = (counts[e.layer] || 0) + 1;
    return LAYER_ORDER.filter((k) => serverLayers.includes(k)).map((k) => {
      const sample = events.find((e) => e.layer === k);
      return {
        key: k,
        label: sample?.layerLabel || k,
        color: sample?.color || '#94A3B8',
        count: counts[k] || 0,
        hidden: hiddenLayers.has(k),
      };
    });
  }, [events, serverLayers, hiddenLayers]);

  const toggleLayer = (k) => setHiddenLayers((prev) => {
    const next = new Set(prev);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });

  const step = (dir) => {
    if (view === 'day') setAnchor((a) => addDays(a, dir));
    else if (view === 'week') setAnchor((a) => addDays(a, dir * 7));
    else if (view === 'month') setAnchor((a) => addMonths(a, dir));
    else setAnchor((a) => addMonths(a, dir * 12));
  };

  const heading = useMemo(() => {
    if (view === 'day') return fmtDateLong(anchor);
    if (view === 'week') {
      const f = startOfWeek(anchor); const t = addDays(f, 6);
      return `${f.toLocaleDateString([], { month: 'short', day: 'numeric' })} – ${t.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}`;
    }
    if (view === 'month') return anchor.toLocaleDateString([], { month: 'long', year: 'numeric' });
    return String(anchor.getFullYear());
  }, [view, anchor]);

  const allClinics = clinicId === 'all';
  const todayKey = dayKey(new Date());

  // ── Event chip (shared across views) ────────────────────────────────
  const EventChip = ({ e, full }) => (
    <button
      onClick={() => setSelected(e)}
      title={e.title}
      style={{
        display: 'flex', alignItems: 'center', gap: 5, width: '100%',
        padding: full ? '6px 8px' : '2px 6px', marginBottom: 2,
        background: '#fff', border: `1px solid ${e.color}33`,
        borderLeft: `3px solid ${e.color}`, borderRadius: 6,
        fontSize: full ? 12 : 11, color: '#374151', cursor: 'pointer',
        textAlign: 'left', overflow: 'hidden',
      }}
    >
      {!e.all_day && <span style={{ color: '#6C757D', flexShrink: 0 }}>{fmtTime(e.start)}</span>}
      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 600 }}>
        {e.title}
      </span>
      {allClinics && e.clinic_name && (
        <span style={{ marginLeft: 'auto', flexShrink: 0, fontSize: 10, color: '#6C757D', display: 'inline-flex', alignItems: 'center', gap: 2 }}>
          <MapPin size={9} />{e.clinic_name}
        </span>
      )}
    </button>
  );

  // ── Views ─────────────────────────────────────────────────────────────
  const renderDay = () => {
    const list = byDay.get(dayKey(anchor)) || [];
    const allDay = list.filter((e) => e.all_day);
    const timed = list.filter((e) => !e.all_day);
    return (
      <div style={{ ...cardWrap, padding: 16 }}>
        {list.length === 0 && <p style={{ color: '#6C757D', fontSize: 13, margin: 0 }}>No events for this day.</p>}
        {allDay.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <div style={sectionLabel}>All-day / deadlines</div>
            {allDay.map((e) => <EventChip key={e.key} e={e} full />)}
          </div>
        )}
        {timed.length > 0 && (
          <div>
            <div style={sectionLabel}>Scheduled</div>
            {timed.map((e) => <EventChip key={e.key} e={e} full />)}
          </div>
        )}
      </div>
    );
  };

  const renderWeek = () => {
    const f = startOfWeek(anchor);
    return (
      <div className="responsive-scroll">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 8, minWidth: 840 }}>
          {WEEKDAYS.map((wd, i) => {
            const d = addDays(f, i);
            const k = dayKey(d);
            const list = byDay.get(k) || [];
            return (
              <div key={wd} style={{ ...cardWrap, padding: 8, minHeight: 180, background: k === todayKey ? '#F0FDFA' : '#F8F9FA' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: k === todayKey ? '#2E8B98' : '#6C757D', marginBottom: 6 }}>
                  {wd} {d.getDate()}
                </div>
                {list.map((e) => <EventChip key={e.key} e={e} />)}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderMonth = () => {
    const first = startOfMonth(anchor);
    const gridStart = startOfWeek(first);
    const cells = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
    return (
      <div className="responsive-scroll">
        <div style={{ minWidth: 840 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 4 }}>
            {WEEKDAYS.map((wd) => (
              <div key={wd} style={{ fontSize: 11, fontWeight: 700, color: '#6C757D', textAlign: 'center' }}>{wd}</div>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
            {cells.map((d) => {
              const k = dayKey(d);
              const inMonth = d.getMonth() === anchor.getMonth();
              const list = byDay.get(k) || [];
              return (
                <div
                  key={k}
                  style={{
                    minHeight: 92, padding: 4, borderRadius: 8,
                    background: k === todayKey ? '#F0FDFA' : inMonth ? '#fff' : '#F8F9FA',
                    boxShadow: neuShadowInset, opacity: inMonth ? 1 : 0.55,
                  }}
                >
                  <button
                    onClick={() => { setAnchor(d); setView('day'); }}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                      fontSize: 11, fontWeight: 700,
                      color: k === todayKey ? '#2E8B98' : '#6C757D', marginBottom: 2,
                    }}
                  >
                    {d.getDate()}
                  </button>
                  {list.slice(0, 3).map((e) => <EventChip key={e.key} e={e} />)}
                  {list.length > 3 && (
                    <button
                      onClick={() => { setAnchor(d); setView('day'); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 10, color: '#2E8B98', fontWeight: 700, padding: 0 }}
                    >
                      +{list.length - 3} more
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderYear = () => {
    const yStart = startOfYear(anchor);
    const months = Array.from({ length: 12 }, (_, i) => addMonths(yStart, i));
    // Per-month, per-layer counts.
    const counts = new Map();
    for (const e of visible) {
      const d = new Date(e.start);
      const mk = `${d.getFullYear()}-${d.getMonth()}`;
      if (!counts.has(mk)) counts.set(mk, {});
      const c = counts.get(mk);
      c[e.layer] = (c[e.layer] || 0) + 1;
    }
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
        {months.map((m) => {
          const mk = `${m.getFullYear()}-${m.getMonth()}`;
          const c = counts.get(mk) || {};
          const total = Object.values(c).reduce((a, b) => a + b, 0);
          return (
            <button
              key={mk}
              onClick={() => { setAnchor(m); setView('month'); }}
              style={{ ...cardWrap, padding: 12, cursor: 'pointer', textAlign: 'left', border: 'none' }}
            >
              <div style={{ fontSize: 13, fontWeight: 700, color: '#212529', marginBottom: 6 }}>
                {m.toLocaleDateString([], { month: 'long' })}
              </div>
              <div style={{ fontSize: 12, color: '#6C757D', marginBottom: 6 }}>
                {total} event{total === 1 ? '' : 's'}
              </div>
              <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                {layerChips.filter((lc) => !lc.hidden && c[lc.key]).map((lc) => (
                  <span key={lc.key} style={{
                    fontSize: 10, fontWeight: 700, color: '#fff', background: lc.color,
                    borderRadius: 999, padding: '1px 7px',
                  }}>
                    {c[lc.key]}
                  </span>
                ))}
              </div>
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div>
      {/* ── Toolbar ─────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Calendar size={18} color="#2E8B98" />
          <span style={{ fontSize: 15, fontWeight: 700, color: '#212529' }}>Operations Calendar</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <button onClick={() => step(-1)} style={navBtn} aria-label="Previous"><ChevronLeft size={14} /></button>
          <button onClick={() => setAnchor(new Date())} style={{ ...navBtn, width: 'auto', padding: '0 10px', fontSize: 12 }}>Today</button>
          <button onClick={() => step(1)} style={navBtn} aria-label="Next"><ChevronRight size={14} /></button>
        </div>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#495057' }}>{heading}</span>

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Clinic dropdown — primary grouping axis per Daniel's spec */}
          <select
            value={clinicId}
            onChange={(e) => setClinicId(e.target.value === 'all' ? 'all' : parseInt(e.target.value, 10))}
            style={{
              padding: '6px 10px', borderRadius: 8, border: '1px solid #DEE2E6',
              background: '#fff', fontSize: 12, color: '#374151', fontWeight: 600,
            }}
            aria-label="Clinic filter"
          >
            <option value="all">All clinics</option>
            {clinics.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          {/* View switcher */}
          <div style={{ display: 'flex', background: '#F8F9FA', borderRadius: 10, boxShadow: neuShadowInset, padding: 2 }}>
            {VIEWS.map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                style={{
                  padding: '5px 12px', border: 'none', borderRadius: 8, cursor: 'pointer',
                  fontSize: 12, fontWeight: view === v ? 700 : 400,
                  background: view === v ? '#fff' : 'transparent',
                  boxShadow: view === v ? neuShadow : 'none',
                  color: view === v ? '#2E8B98' : '#6C757D', textTransform: 'capitalize',
                }}
              >
                {v}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Layer filter chips ───────────────────────────────────────── */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginBottom: 12 }}>
        <LayersIcon size={13} color="#6C757D" />
        {layerChips.map((lc) => (
          <button
            key={lc.key}
            onClick={() => toggleLayer(lc.key)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '3px 10px', borderRadius: 999, cursor: 'pointer',
              border: `1px solid ${lc.color}`, fontSize: 11, fontWeight: 700,
              background: lc.hidden ? 'transparent' : lc.color,
              color: lc.hidden ? lc.color : '#fff',
              opacity: lc.hidden ? 0.6 : 1,
            }}
            aria-pressed={!lc.hidden}
          >
            {lc.label} · {lc.count}
          </button>
        ))}
        {loading && <span style={{ fontSize: 11, color: '#6C757D' }}>Loading…</span>}
        {error && <span style={{ fontSize: 11, color: '#DC2626' }}>{error}</span>}
      </div>

      {/* ── Active view ──────────────────────────────────────────────── */}
      {view === 'day' && renderDay()}
      {view === 'week' && renderWeek()}
      {view === 'month' && renderMonth()}
      {view === 'year' && renderYear()}

      {/* ── Event detail modal ───────────────────────────────────────── */}
      {selected && (
        <div
          role="dialog" aria-modal="true"
          onClick={() => setSelected(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(17,24,39,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
          }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{
            background: '#fff', borderRadius: 14, maxWidth: 420, width: '100%', padding: 20,
            boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <span style={{ width: 10, height: 10, borderRadius: 999, background: selected.color, flexShrink: 0 }} />
              <span style={{ fontSize: 11, fontWeight: 700, color: '#6C757D', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {selected.layerLabel}{selected.status ? ` · ${selected.status}` : ''}
              </span>
              <button onClick={() => setSelected(null)} aria-label="Close" style={{ ...navBtn, marginLeft: 'auto' }}><X size={13} /></button>
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#212529', marginBottom: 8 }}>{selected.title}</div>
            <div style={{ fontSize: 12, color: '#495057', display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span>
                {selected.all_day
                  ? fmtDateLong(selected.start)
                  : `${fmtDateLong(selected.start)} · ${fmtTime(selected.start)}${selected.end ? ` – ${fmtTime(selected.end)}` : ''}`}
              </span>
              {selected.clinic_name && <span><MapPin size={11} style={{ verticalAlign: 'middle' }} /> {selected.clinic_name}</span>}
              {selected.contact_name && <span>Patient/contact: <strong>{selected.contact_name}</strong></span>}
              {selected.meta?.priority && <span>Priority: {selected.meta.priority}</span>}
              {selected.meta?.kind && <span>Logistics: {selected.meta.kind}{selected.meta.subtype ? ` / ${selected.meta.subtype}` : ''}</span>}
              {selected.meta?.value != null && <span>Deal value: ${Number(selected.meta.value).toLocaleString()}</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const navBtn = {
  width: 28, height: 28, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  border: '1px solid #DEE2E6', borderRadius: 8, background: '#fff', color: '#6C757D', cursor: 'pointer',
};
const sectionLabel = {
  fontSize: 11, fontWeight: 700, color: '#6C757D', textTransform: 'uppercase',
  letterSpacing: '0.04em', marginBottom: 6,
};
